import { Option } from '../domain/schemas';

export type BuildColumnsOrderByPatternOrderOptions = {
  /**
   * 左端から固定する列数（sticky 列数と一致させる）。
   * @default 1（従来の「先頭1列固定」と同一）
   */
  fixedLeftCount?: number;
};

/**
 * パターン適用時の列順を構築する（機種別商圏・減価償却等で共通利用）
 *
 * Why:
 * - 表示項目パターン適用時に patternFieldsOrder をそのまま採用すると、
 *   パターン未登録の固定先頭列（機種名列等）が columnsOrder から欠落してしまうため。
 *
 * What:
 * - 先頭 `fixedLeftCount` 列を `columnsOrder` の順のまま保持する。
 * - その後ろに、パターン順の列を続けて配置する（固定列コードはパターン側から除外）。
 * - パターンに含まれない既存列は末尾へ残す。
 *
 * @param patternFieldsOrder 適用したパターンの fields 配列（列順の候補）
 * @param columnsOrder APIレスポンスから得た現在の列順コード配列
 */
export const buildColumnsOrderByPatternOrder = (
  patternFieldsOrder: string[],
  columnsOrder: string[],
  options?: BuildColumnsOrderByPatternOrderOptions
): string[] => {
  const fixedLeftCount = options?.fixedLeftCount ?? 1;
  const fixedPrefix = columnsOrder.slice(0, fixedLeftCount);
  const fixedCodeSet = new Set(fixedPrefix);

  const patternOrderedColumns = patternFieldsOrder.filter(
    (fieldCode) => !fixedCodeSet.has(fieldCode)
  );
  const columnsNotInPattern = columnsOrder.filter(
    (fieldCode) =>
      !fixedCodeSet.has(fieldCode) && !patternOrderedColumns.includes(fieldCode)
  );

  return fixedPrefix.length === 0
    ? [...patternOrderedColumns, ...columnsNotInPattern]
    : [...fixedPrefix, ...patternOrderedColumns, ...columnsNotInPattern];
};

/**
 * 手動選択時の列順を構築する（新規追加項目を先頭付近に挿入する既存ロジック・機種別商圏・減価償却等で共通利用）
 *
 * @param selectedFieldCodes 選択中の全表示項目コード配列
 * @param columnsOrder APIレスポンスから得た現在の列順コード配列
 * @param masterFieldOrder マスタの表示項目（新規追加項目のソート順計算に使用。{ code: string }[] 形式）
 */
export const buildColumnsOrderByManualSelection = (
  selectedFieldCodes: string[],
  columnsOrder: string[],
  masterFieldOrder: { code: string }[]
): string[] => {
  const newlyAddedFieldCodes = selectedFieldCodes.filter(
    (field) => !columnsOrder.includes(field)
  );

  const desiredOrder = masterFieldOrder.map(({ code }) => code);
  const sortedNewFieldCodes = [...newlyAddedFieldCodes].sort(
    (leftFieldCode, rightFieldCode) =>
      desiredOrder.indexOf(leftFieldCode) < desiredOrder.indexOf(rightFieldCode)
        ? -1
        : 1
  );

  const [fixedFirstColumnCode, ...rest] = columnsOrder;
  //fieldsが[]の時にデフォルトの表示項目が表示されるよう、空の配列を入れる
  return selectedFieldCodes.length
    ? [fixedFirstColumnCode, ...sortedNewFieldCodes, ...rest]
    : [];
};

/**
 * 固定先頭列がない場合の手動選択時列順（モード項目など）
 * 新規追加項目を先頭に配置し、既存列は columnsOrder の順序を維持する
 */
const buildColumnsOrderByManualSelectionWithoutFixedFirstColumn = (
  selectedFieldCodes: string[],
  columnsOrder: string[],
  masterFieldOrder: { code: string }[]
): string[] => {
  const newlyAddedFieldCodes = selectedFieldCodes.filter(
    (field) => !columnsOrder.includes(field)
  );
  const desiredOrder = masterFieldOrder.map(({ code }) => code);
  const sortedNewFieldCodes = [...newlyAddedFieldCodes].sort((a, b) => {
    return desiredOrder.indexOf(a) < desiredOrder.indexOf(b) ? -1 : 1;
  });
  const existingColumnsInOrder = columnsOrder.filter((code) =>
    selectedFieldCodes.includes(code)
  );
  return selectedFieldCodes.length
    ? [...sortedNewFieldCodes, ...existingColumnsInOrder]
    : [];
};

export type BuildColumnsOrderOptions = {
  /**
   * 手動選択時に先頭列を固定列として扱うか。
   * false の場合（モード項目など）、新規追加項目を先頭に配置する。
   * @default true
   */
  hasFixedFirstColumn?: boolean;
  /**
   * パターン適用時のみ。`buildColumnsOrderByPatternOrder` に渡す固定左列数。
   * @default 1
   */
  patternFixedLeftCount?: number;
};

/**
 * 表示項目変更後の列順を計算する（パターン適用時と手動選択時で振り分け）
 *
 * @param fieldsOrder パターン適用時の列順候補。手動選択時は `undefined`
 * @param selectedFieldCodes 選択中の全表示項目コード配列
 * @param columnsOrder APIレスポンスから得た現在の列順コード配列
 * @param masterFieldOrder マスタの表示項目（手動選択時の desiredOrder 計算に使用）
 * @param options `hasFixedFirstColumn`: false のとき固定先頭列なし扱い。`patternFixedLeftCount`: パターン適用時の固定左列数（既定 1）
 */
export const buildColumnsOrder = (
  fieldsOrder: string[] | undefined,
  selectedFieldCodes: string[],
  columnsOrder: string[],
  masterFieldOrder: { code: string }[],
  options?: BuildColumnsOrderOptions
): string[] => {
  if (fieldsOrder && fieldsOrder.length > 0) {
    return buildColumnsOrderByPatternOrder(fieldsOrder, columnsOrder, {
      fixedLeftCount: options?.patternFixedLeftCount ?? 1,
    });
  }
  const hasFixedFirstColumn = options?.hasFixedFirstColumn ?? true;
  if (!hasFixedFirstColumn) {
    return buildColumnsOrderByManualSelectionWithoutFixedFirstColumn(
      selectedFieldCodes,
      columnsOrder,
      masterFieldOrder
    );
  }
  return buildColumnsOrderByManualSelection(
    selectedFieldCodes,
    columnsOrder,
    masterFieldOrder
  );
};

/**
 * Why:
 * - 表示項目パターンの保存・適用時に、利用者が見ている列順を維持するため。
 *
 * What:
 * - `fieldCodesInOrder` の順序に従って `options` から `Option` を抽出して返す。
 * - `options` 側に存在しないコードは除外する。
 *
 * @param fieldCodesInOrder 並び順の基準となる表示項目コード配列（呼び出し元の selectedPatternFieldsOrder 等）
 * @param options 抽出対象の表示項目配列
 * @returns `fieldCodesInOrder` の順序に従って整列された `Option[]`
 */
export const pickOptionsByFieldCodesInOrder = (
  fieldCodesInOrder: string[],
  options: Option[]
): Option[] => {
  const optionByCode = new Map(options.map((option) => [option.code, option]));

  return fieldCodesInOrder
    .map((fieldCode) => optionByCode.get(fieldCode))
    .filter((option): option is Option => option !== undefined);
};

/**
 * Why:
 * - パターン適用後に手動で項目を追加・削除した場合や、パターンの `fields` に含まれる
 *   コードが現在の選択可能項目に存在しない場合、古い `fields` 順序をそのまま渡すと
 *   無効なフィールドコードが Redux state や API リクエストに混入するため。
 * - 同じ判定ロジックが複数コンポーネントで重複するのを防ぐため。
 *
 * What:
 * - 「直前に適用したパターンの `fields`（= 一時保持している列順）」が
 *   現在の選択項目と完全一致するかを判定する。
 * - 型述語 (`patternFieldsOrder is string[]`) により、戻り値が `true` の場合は
 *   呼び出し元で `patternFieldsOrder` が `string[]` として型絞り込みされる。
 *
 * 直前に適用したパターンの `fields` 配列が、現在の選択項目と過不足なく一致するかを返す。
 *
 * 判定条件（すべて満たす場合に `true`）:
 * 1. `patternFieldsOrder` が非 `null`/`undefined`
 * 2. 長さが `selectedFieldCodes` と等しい
 * 3. `selectedFieldCodes` のすべての要素が `patternFieldsOrder` に含まれる
 *
 * @param patternFieldsOrder 直前に適用したパターンの `fields`（項目コードを保存順に並べた配列）。`undefined` の場合は `false` を返す
 * @param selectedFieldCodes 現在の選択項目コード配列
 * @returns 過不足なく一致する場合 `true`（`patternFieldsOrder` は `string[]` に絞り込まれる）
 */
export const hasPatternFieldsOrderExactMatch = (
  patternFieldsOrder: string[] | undefined,
  selectedFieldCodes: string[]
): patternFieldsOrder is string[] =>
  patternFieldsOrder != null &&
  patternFieldsOrder.length === selectedFieldCodes.length &&
  selectedFieldCodes.every((fieldCode) =>
    patternFieldsOrder.includes(fieldCode)
  );

/**
 * Why:
 * - 表示項目パターンを保存する際、選択項目コードを「現在のテーブル表示順」と同じ並びで
 *   保存できるようにするため（次回適用時にユーザーが見ている列順を再現するため）。
 * - 並び替えロジックを1箇所に集約し、将来の画面追加時にも再利用できるようにするため。
 *
 * What:
 * - `selectedFieldCodes` を `columnsOrder` の位置順に従って並び替えて返す。
 *
 * 並び替えルール:
 * - 両方とも `columnsOrder` に含まれる場合は、そのインデックス順 小さい方が前に配置
 * - 片方のみ含まれる場合は、含まれる方を前に配置
 * - どちらも含まれない場合は、元の相対順を維持
 *
 * 例:
 * - `selectedFieldCodes = ['稼働率', '粗利', '売上']`, `columnsOrder = ['売上', '粗利', '稼働率']`
 *   -> `['売上', '粗利', '稼働率']`
 * - `selectedFieldCodes = ['売上', '粗利']`, `columnsOrder = ['売上', '粗利', 'SIS 総台数']`
 *   -> `['売上', '粗利']`（選択項目が列順定義に含まれている通常ケース）
 * - `selectedFieldCodes = ['旧項目A', '売上']`, `columnsOrder = ['売上', '粗利']`
 *   -> `['旧項目A', '売上']`（古いパターン等で一部不一致がある場合）
 *
 * @param selectedFieldCodes 並び替え対象の表示項目コード配列（呼び出し元の selectFields から抽出した code 配列）
 * @param columnsOrder テーブル上の現在の列順コード配列（未指定/空配列可）
 * @param masterFieldOrder マスタの表示項目（適用前保存時、columnsOrderに含まれない新規項目を表示項目順でソートする際に使用）
 * @returns 列順ルールを反映した表示項目コード配列
 */
export const sortFieldCodesByColumnsOrder = (
  selectedFieldCodes: string[],
  columnsOrder?: string[],
  masterFieldOrder?: { code: string }[]
): string[] => {
  // 列順が未指定の場合は元の順序を返す
  if (!columnsOrder || columnsOrder.length === 0) {
    return [...selectedFieldCodes];
  }

  const existingSelectedFieldCodes = selectedFieldCodes.filter((fieldCode) =>
    columnsOrder.includes(fieldCode)
  );
  const newSelectedFieldCodes = selectedFieldCodes.filter(
    (fieldCode) => !columnsOrder.includes(fieldCode)
  );

  // 適用前保存時：新規追加項目を表示項目順（マスタ順）でソート
  const sortedNewSelectedFieldCodes =
    masterFieldOrder != null && masterFieldOrder.length > 0
      ? (() => {
          const masterPositionByCode = new Map(
            masterFieldOrder.map((field, index) => [field.code, index])
          );
          return [...newSelectedFieldCodes].sort(
            (leftFieldCode, rightFieldCode) =>
              (masterPositionByCode.get(leftFieldCode) ??
                Number.MAX_SAFE_INTEGER) -
              (masterPositionByCode.get(rightFieldCode) ??
                Number.MAX_SAFE_INTEGER)
          );
        })()
      : newSelectedFieldCodes;

  // 列順に従って表示項目コードを並び替える
  const columnPositionByFieldCode = new Map(
    columnsOrder.map((fieldCode, index) => [fieldCode, index])
  );

  const sortedExistingFieldCodes = [...existingSelectedFieldCodes].sort(
    (leftFieldCode, rightFieldCode) =>
      (columnPositionByFieldCode.get(leftFieldCode) ??
        Number.MAX_SAFE_INTEGER) -
      (columnPositionByFieldCode.get(rightFieldCode) ?? Number.MAX_SAFE_INTEGER)
  );
  const sortedFieldCodes = [
    ...sortedNewSelectedFieldCodes,
    ...sortedExistingFieldCodes,
  ];
  return sortedFieldCodes;
};
